<template>
  <div class="relative col-span-full grid w-full grid-cols-subgrid space-y-2">
    <button
      type="button"
      class="col-span-full grid w-full min-w-0 grid-cols-2 items-start gap-x-4 gap-y-2 rounded-md bg-white p-4 text-left md:grid-cols-subgrid md:items-center md:gap-6 md:gap-x-6 md:gap-y-0"
      @click="$emit('click')"
    >
      <div>
        <span class="font-semibold">{{
          invoice.completeInvoiceNumber ?? t('invoiceNrNotAvailable')
        }}</span>
      </div>
      <div
        class="col-span-2 min-w-0 break-words max-md:col-start-1 max-md:row-start-2 md:col-span-1"
      >
        {{ invoice.title || '-' }}
      </div>
      <div>
        {{ invoice.invoiceDate ? formatDate(invoice.invoiceDate, dateFormat) : '-' }}
      </div>
      <div class="text-right">
        <span v-currency="invoice.totalPositionSum" class="font-bold tabular-nums" />
        <div class="text-xs text-obsidian-500">
          {{ t('net') }}: <span v-currency="totalNetPrice" class="tabular-nums" />
        </div>
      </div>
      <div class="flex h-full items-center gap-4">
        <AllocationStatusIndicator
          v-if="showAllocatedAmounts && invoice.totalAllocatedAmount != null"
          class="max-md:col-start-2 max-md:row-start-4"
          :is-loading="false"
          :allocated-amount="invoice.totalAllocatedAmount"
          :total-amount="invoice.totalPositionSum"
          type="invoice"
        />
        <InvoiceStatusIndicator
          :total-amount="invoice.totalPositionSum"
          :unpaid-amount="invoice.currentBalance"
          :sent-state-date="invoice.sentStateDate"
        />
        <div v-if="invoice.status !== InvoiceStatus.OPEN" class="md:text-right">
          <span
            class="rounded-sm px-2 py-0.5 text-sm"
            :class="{
              'bg-yellow-100 text-yellow-800': invoice.status !== InvoiceStatus.CANCELED,
              'bg-red-100 text-red-800': invoice.status === InvoiceStatus.CANCELED,
            }"
          >
            {{ t(`invoiceStatus.${invoice.status}`) }}
          </span>
        </div>
      </div>
      <div class="flex justify-end max-md:col-start-2 max-md:row-start-1" @click.stop>
        <DropdownDotted
          :buttons="dropdownButtons"
          :is-loading="isDeleting || isUpdating || isSending"
        />
      </div>
    </button>
    <InvoicesForm
      v-if="(editing || defaultExpanded) && invoice.status === InvoiceStatus.DRAFT"
      :booking="booking"
      :invoices="invoices"
      :payments="payments"
      :invoice="invoice"
      class="col-span-full"
      @on-submit="handleUpdateInvoice"
      @on-cancel="editing = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbility } from '@casl/vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import type { CreateInvoiceDto, Invoice } from '@/entities/invoices/invoice.entity';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useDeleteInvoice, useUpdateInvoice } from '@/queries/use-invoices';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import { Alert } from '@/services/alert.service';
import DropdownDotted from '../DropdownDotted.vue';
import type { DropdownItem } from '../Dropdown.vue';
import InvoicesForm from './InvoicesForm.vue';
import AllocationStatusIndicator from './AllocationStatusIndicator.vue';
import { useDateFormat } from '@/hooks/use-date-format';
import InvoiceStatusIndicator from './InvoiceStatusIndicator.vue';
import { subject } from '@casl/ability';

const props = defineProps<{
  invoice: Invoice;
  defaultExpanded?: boolean;
  booking: Booking;
  invoices: Invoice[];
  payments: Payment[];
  showAllocatedAmounts?: boolean;
}>();

defineEmits<{
  (e: 'click'): void;
}>();

const { t } = useI18n();
const { can } = useAbility();
const dateFormat = useDateFormat();

const editing = ref(false);

const { mutateAsync: updateInvoice, isPending: isUpdating } = useUpdateInvoice();
const { mutateAsync: deleteInvoice, isPending: isDeleting } = useDeleteInvoice();
const { downloadInvoice, downloadInvoiceDirect, sendInvoiceToCustomerWithAlert, isSending } =
  useInvoicePdfs();

const totalNetPrice = computed(() => {
  return Number(
    props.invoice.positions
      .reduce(
        (sum, position) => sum + (position.amount * position.unitPrice) / (1 + position.tax),
        0,
      )
      .toFixed(2),
  );
});

const handleUpdateInvoice = async (invoiceData: CreateInvoiceDto, sendInvoiceOnSave: boolean) => {
  await updateInvoice({
    id: props.invoice.id,
    invoice: invoiceData,
  });
  if (sendInvoiceOnSave && invoiceData.status !== InvoiceStatus.DRAFT) {
    await sendInvoiceToCustomerWithAlert(props.invoice.id);
  }
  editing.value = false;
};

const cancelInvoice = async () => {
  const alertResult = await Alert.fire({
    titleText: t('reallyCancelInvoiceTitle'),
    text: t('reallyCancelInvoiceText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('cancelInvoice'),
    denyButtonText: t('dontCancel'),
  });
  if (alertResult.isConfirmed) {
    await updateInvoice({
      id: props.invoice.id,
      invoice: { status: InvoiceStatus.CANCELED },
    });
  }
};

const removeInvoice = async () => {
  const alertResult = await Alert.fire({
    titleText: t('reallyRemoveInvoiceTitle'),
    text: t('reallyRemoveInvoiceText'),
    icon: 'warning',
    buttons: [
      {
        text: t('removeInvoice'),
        propertyName: 'isConfirmed',
        style: 'danger',
      },
      {
        text: t('keep'),
        propertyName: 'isDenied',
        style: 'ghost',
      },
    ],
  });
  if (alertResult.isConfirmed) {
    deleteInvoice(props.invoice.id);
  }
};

const markInvoiceAsSent = async () => {
  await updateInvoice({
    id: props.invoice.id,
    invoice: { sentStateDate: new Date().toISOString() },
  });
};

const dropdownButtons = computed<DropdownItem[]>(() =>
  props.invoice.status === InvoiceStatus.DRAFT
    ? [
        ...(props.defaultExpanded
          ? []
          : [
              {
                title: t('editInvoice'),
                onClick: () => (editing.value = true),
              },
            ]),
        {
          title: t('removeInvoice'),
          onClick: removeInvoice,
        },
      ]
    : [
        {
          title: t('download'),
          onClick: () => downloadInvoice(props.invoice),
        },
        {
          title: t('downloadDirect'),
          onClick: () => downloadInvoiceDirect(props.invoice),
        },
        {
          title: t('sendToCustomer'),
          onClick: () => sendInvoiceToCustomerWithAlert(props.invoice.id),
        },
        ...(!can('update', subject('Invoice', props.invoice), 'sentStateDate') ||
        props.invoice.sentStateDate
          ? []
          : [
              {
                title: t('markAsSent'),
                onClick: markInvoiceAsSent,
              },
            ]),
        ...(props.invoice.status === InvoiceStatus.CANCELED
          ? []
          : [
              {
                title: t('cancelInvoice'),
                onClick: cancelInvoice,
              },
            ]),
      ],
);
</script>

<i18n lang="json">
{
  "en": {
    "invoiceNrNotAvailable": "INr. n/a",
    "editInvoice": "Edit Invoice",
    "removeInvoice": "Remove Invoice",
    "download": "Show PDF",
    "downloadDirect": "Download PDF",
    "sendToCustomer": "Send to Customer",
    "markAsSent": "Mark as sent",
    "cancelInvoice": "Cancel Invoice",
    "reallyRemoveInvoiceTitle": "Really remove Invoice?",
    "reallyRemoveInvoiceText": "The Invoice will be permanently removed.",
    "reallyCancelInvoiceTitle": "Really cancel Invoice?",
    "reallyCancelInvoiceText": "The Invoice will be permanently canceled.",
    "keep": "Don't remove",
    "dontCancel": "Don't cancel",
    "allocated": "All.",
    "net": "Net"
  },
  "de": {
    "invoiceNrNotAvailable": "RNr. n/a",
    "editInvoice": "Rechnung bearbeiten",
    "removeInvoice": "Löschen",
    "download": "Anzeigen (PDF)",
    "downloadDirect": "Herunterladen (PDF)",
    "sendToCustomer": "An Kunde senden",
    "markAsSent": "Als versendet markieren",
    "cancelInvoice": "Rechnung stornieren",
    "reallyRemoveInvoiceTitle": "Rechnung wirklich löschen?",
    "reallyRemoveInvoiceText": "Die Rechnung wird unwiderruflich gelöscht.",
    "reallyCancelInvoiceTitle": "Rechnung wirklich stornieren?",
    "reallyCancelInvoiceText": "Die Rechnung wird unwiderruflich storniert.",
    "keep": "Nicht löschen",
    "dontCancel": "Nicht stornieren",
    "allocated": "Zug.",
    "net": "Netto"
  },
  "es": {
    "invoiceNrNotAvailable": "N.º fact. s/d",
    "editInvoice": "Editar factura",
    "removeInvoice": "Eliminar factura",
    "download": "Ver PDF",
    "downloadDirect": "Descargar PDF",
    "sendToCustomer": "Enviar al cliente",
    "markAsSent": "Marcar como enviada",
    "cancelInvoice": "Anular factura",
    "reallyRemoveInvoiceTitle": "¿Eliminar la factura?",
    "reallyRemoveInvoiceText": "La factura se eliminará de forma permanente.",
    "reallyCancelInvoiceTitle": "¿Anular la factura?",
    "reallyCancelInvoiceText": "La factura se anulará de forma permanente.",
    "keep": "No eliminar",
    "dontCancel": "No anular",
    "allocated": "Impt.",
    "net": "Neto"
  }
}
</i18n>
